import { transform, isEqual, isObject, findIndex } from 'lodash';
import {
  PHENOTYPES_CONSTANTS,
  HPO_CONSTANTS,
  CONSTANTS
} from 'src/components/Constants';
import { GENDER } from 'src/components/questionnaire/Constants';

// function to capitalize the input
export const capitalizeLetter = (word) => {
  if (typeof word !== 'string') return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
};

// HGNC search results are stored on genotypes as "{SYMBOL} — {full name}",
// e.g. "JAK2 — Janus kinase 2". Many places want the parts shown separately
// (bold symbol with the descriptive name underneath). Older records may have
// only the bare symbol — handle both. The em-dash separator is U+2014 with
// a space either side, matching the HGNC seed format.
export const parseGeneLabel = (label) => {
  if (!label || typeof label !== 'string') {
    return { symbol: '', description: '' };
  }
  const idx = label.indexOf(' — ');
  if (idx === -1) {
    return { symbol: label.trim(), description: '' };
  }
  return {
    symbol: label.slice(0, idx).trim(),
    description: label.slice(idx + 3).trim()
  };
};

// Resolve a genotype's display parts. Prefers the structured `symbol` /
// `description` fields when populated (new records and migrated records),
// otherwise falls back to parsing the legacy combined `name`. Always
// returns both fields as strings.
export const getGenotypeDisplayParts = (genotype) => {
  if (!genotype || typeof genotype !== 'object') {
    return { symbol: '', description: '' };
  }
  const hasStructured =
    (genotype.symbol && genotype.symbol.trim()) ||
    (genotype.description && genotype.description.trim());
  if (hasStructured) {
    return {
      symbol: (genotype.symbol || '').trim(),
      description: (genotype.description || '').trim()
    };
  }
  return parseGeneLabel(genotype.name);
};

export const scrollToTop = (top = 0) => {
  window.scrollTo({
    top: top,
    left: 0,
    behavior: 'smooth'
  });
};

// If any of field have error then return true-
export const inverseValidationLogicForPersonDetails = (
  formValues,
  labels,
  isEnablePregnancy
) => {
  for (let key in labels) {
    // check for required validation if field is required & field is enabled
    let required = labels[key].required && labels[key].enabled;
    if (required) {
      // Check pregnancy is required or not-
      if (key === 'pregnancy') {
        if (
          isEnablePregnancy &&
          (formValues.gender === GENDER.female ||
            formValues.gender === GENDER.unknown)
        ) {
          let value = formValues['isPregnancies'];
          if (!value) {
            return true;
          }
        }
      } else {
        let value = formValues[key];
        if (!value) {
          return true;
        }
      }
    }
  }

  return false;
};

// Returns an array of display label strings for required fields that are missing
export const getMissingPersonDetailFields = (
  formValues,
  labels,
  isEnablePregnancy
) => {
  const missing = [];
  for (let key in labels) {
    let required = labels[key].required && labels[key].enabled;
    if (required) {
      if (key === 'pregnancy') {
        if (
          isEnablePregnancy &&
          (formValues.gender === GENDER.female ||
            formValues.gender === GENDER.unknown)
        ) {
          if (!formValues['isPregnancies']) {
            missing.push(labels[key].label || key);
          }
        }
      } else {
        if (!formValues[key]) {
          missing.push(labels[key].label || key);
        }
      }
    }
  }
  return missing;
};

export const calculateAge = (dob, dod = null) => {
  if (dob) {
    let birthday = dob;
    if (typeof dob === 'string') birthday = new Date(dob);

    // Same guard as parseDate: an unreadable stored date must degrade to blank
    // rather than surface as the string "NaN" in an age column.
    if (isNaN(birthday?.getTime?.())) return '';

    let today = Date.now();
    // if date of death is provided, use that to calculate diff
    if (dod) today = new Date(dod);
    if (isNaN(new Date(today).getTime())) return '';

    const month_diff = today - birthday.getTime();
    const age_dt = new Date(month_diff);
    return Math.abs(age_dt.getUTCFullYear() - 1970);
  } else return '';
};

/**
 * Precision-aware age calculation.
 *
 * Accepts `PartialDate` objects (from `src/util/partialDate.js`) and returns
 * an `AgeResult` that carries the precision of the inputs. Consumers that
 * render a number can call `formatAgeDisplay()` to get the right shape
 * (`"45"` exact, `"~45"` approximate, `"45–46"` ranged).
 *
 * @typedef {Object} AgeResult
 * @property {number} age          Best-estimate age in whole years.
 * @property {number} ageMin       Minimum possible age given precision.
 * @property {number} ageMax       Maximum possible age given precision.
 * @property {string} precision    `'full' | 'month' | 'year'` — least-precise input.
 * @property {boolean} isExact     True only when both inputs are full precision.
 *
 * @param {import('./partialDate').PartialDate|null|undefined} dob
 * @param {import('./partialDate').PartialDate|null|undefined} [dod]
 *   When omitted, today is used as the reference (current age).
 * @returns {AgeResult|null}
 */
export const calculateAgePartial = (dob, dod = null) => {
  if (!dob) return null;

  const ref = dod || {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
    precision: 'full'
  };

  const rank = { full: 2, month: 1, year: 0 };
  const worstPrecision =
    rank[dob.precision] <= rank[ref.precision] ? dob.precision : ref.precision;

  if (worstPrecision === 'year') {
    const base = ref.year - dob.year;
    return {
      age: base,
      ageMin: Math.max(0, base - 1),
      ageMax: base,
      precision: 'year',
      isExact: false
    };
  }

  if (worstPrecision === 'month') {
    const dobMonth = dob.month ?? 1;
    const refMonth = ref.month ?? 1;
    let age = ref.year - dob.year;
    if (refMonth < dobMonth) age--;
    return {
      age: Math.max(0, age),
      ageMin: Math.max(0, age),
      ageMax: Math.max(0, age + 1),
      precision: 'month',
      isExact: false
    };
  }

  const dobMonth = dob.month ?? 1;
  const dobDay = dob.day ?? 1;
  const refMonth = ref.month ?? 1;
  const refDay = ref.day ?? 1;
  let age = ref.year - dob.year;
  if (refMonth < dobMonth || (refMonth === dobMonth && refDay < dobDay)) {
    age--;
  }
  age = Math.max(0, age);
  return {
    age,
    ageMin: age,
    ageMax: age,
    precision: 'full',
    isExact: true
  };
};

/**
 * Render an `AgeResult` for display.
 *
 *   exact → "45"
 *   month → "~45"
 *   year  → "45–46" (range when ageMin !== ageMax)
 *   null  → ""
 *
 * @param {AgeResult|null|undefined} result
 * @returns {string}
 */
export const formatAgeDisplay = (result) => {
  if (!result) return '';
  if (result.isExact) return String(result.age);
  if (result.precision === 'year' && result.ageMin !== result.ageMax) {
    return `${result.ageMin}–${result.ageMax}`;
  }
  return `~${result.age}`;
};

export const downloadURI = (uri, fileName) => {
  const a = document.getElementById('img-download');
  a.href = uri;
  if (fileName) a.download = fileName;
  a.click();
};

export const downloadXML = (data, fileName) => {
  const blob = new Blob([data], { type: 'text/xml' });
  const uri = URL.createObjectURL(blob);
  downloadURI(uri, `${fileName}.xml`);
};

export const downloadFile = (data, fileName, fileType) => {
  const blob = new Blob([data], { type: `text/${fileType}` });
  const uri = URL.createObjectURL(blob);
  downloadURI(uri, `${fileName}.${fileType}`);
};

export const validatePhn = (value) => {
  const re = /^[0-9*)(+. -]+$/g;
  return re.test(value);
};

export const validateNumber = (value) => {
  const re = /^[0-9]*$/g;
  return re.test(value);
};

export const validateAge = (value) => {
  return validateNumber(value) && parseInt(value) < 200;
};

export const validateDate = (value) => {
  return (
    Object.prototype.toString.call(value) === '[object Date]' &&
    !isNaN(value.getTime())
  );
};

// These three all did `new Date(value)` and formatted the result unconditionally,
// so any value JS could not parse was rendered as "NaN/NaN/NaN" (or "NaN-NaN-NaN",
// or "NaN") rather than as blank. A stored date the app cannot read is a data
// problem to fix at the source — see backend/util/normalizeImportDate.js — but the
// display layer should degrade to empty, the same as a missing date, instead of
// printing NaN at a clinician.
export const parseDate = (date, { parseYear } = {}) => {
  if (date) {
    date = new Date(date);
    if (isNaN(date.getTime())) return '';
    if (parseYear) return date.getFullYear();
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  } else return '';
};

export const formatDate = (value) => {
  if (value) {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  } else return '';
};

export const parseYear = (date) => {
  if (date) {
    date = new Date(date);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear()}`;
  } else return '';
};

export const validateEmail = (email) => {
  if (email) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.toLowerCase());
  }

  return false;
};

export const copyToClipBoard = (value) => {
  if (window.isSecureContext && navigator.clipboard) {
    navigator.clipboard.writeText(value).catch(() => {});
  }
  // fallback copy to clipboard
  else if (window.clipboardData && window.clipboardData.setData) {
    window.clipboardData.setData('Text', value);
  } else {
    const textarea = document.createElement('textarea');
    textarea.textContent = value;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      // nothing to do
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

// function to remove blank values and keys of removals array based on boolean
export const cleanFormData = (
  formValues,
  removals = [],
  onlyRemovals = false
) => {
  const data = {};
  for (const key in formValues) {
    // if the key is not included in removals and value exists for it
    if (!removals.includes(key) && (onlyRemovals || !!formValues[key])) {
      data[key] = formValues[key];
    }
  }
  return data;
};

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function compareObjects(object, base) {
  return transform(object, (result, value, key) => {
    if (!isEqual(value, base[key])) {
      result[key] =
        isObject(value) && isObject(base[key])
          ? compareObjects(value, base[key])
          : value;
    }
  });
}

/**
 * Diff between two arrays, this will return whole array, even if one object is changed
 * @param {Array} updated New updated value
 * @param {Array} previous Previous value (in recent changes)
 */
export function compareArrays(updated, previous) {
  return updated.filter((x) => {
    return findIndex(previous, x) === -1;
  });
}

/**
 * Merge two object
 * @param  {Object} obj1  Object defaultValues
 * @param  {Object} obj2  Object values from DB
 * @return {Object}       Return a new object who represent the merge of obj1 & obj2
 */
export const mergeObjects = (obj1 = {}, obj2 = {}) => {
  const mergedObj = {};

  // Iterate through the keys in obj1
  for (let key in obj1) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj2.hasOwnProperty(key)) {
      // If the key exists in both objects, merge the properties
      mergedObj[key] = {
        ...obj1[key],
        ...obj2[key]
      };
    } else {
      // If the key only exists in obj1, add it to the merged object
      mergedObj[key] = obj1[key];
    }
  }

  return mergedObj;
};

export const passwordRandomChars =
  '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// regex to check for atleast 8 chars, small, caps, 1+ numeber & 1+ special char
export function validatePasswordStrength(value) {
  const re = /^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9 ]).*/;
  return re.test(value);
}

export function injectGoogleCaptchaScript(callback, siteKey) {
  const script = document.createElement('script');
  script.id = 'g-recaptcha';
  script.src = `https://www.google.com/recaptcha/api.js?trustedtypes=true&render=${siteKey}`;
  if (callback) {
    script.onload = callback;
  }
  document.body.appendChild(script);
}

export const showPregnanciesSection = (isEnablePregnancy, personalDetails) => {
  return (
    isEnablePregnancy &&
    personalDetails?.gender !== 'male' &&
    personalDetails?.isPregnancies === 'true'
  );
};

export const getLabelFromValueOptions = (value, options) => {
  if (!value || value === '') {
    return '';
  }

  if (!options) return '';
  let option = options.find((o) => o.value == value);
  if (!option) return '';

  return option.label;
};

export const getColor = (seed) => {
  const range = {
    min: 150,
    max: 255
  };

  const randomColorValue = () =>
    Math.floor(Math.random() * (range.max - range.min + 1) + range.min);

  const seededColorValue = (value) => {
    const normalized = String(value || '');
    let hash = 0;
    for (let i = 0; i < normalized.length; i += 1) {
      hash = (hash << 5) - hash + normalized.charCodeAt(i);
      hash &= hash;
    }
    return Math.abs(hash % (range.max - range.min + 1)) + range.min;
  };

  const uniqueColor = () =>
    `rgb(${randomColorValue()}, ${randomColorValue()}, ${randomColorValue()})`;

  if (seed === undefined || seed === null || seed === '') {
    return uniqueColor();
  }

  const seedString = String(seed);
  return `rgb(${seededColorValue(seedString)}, ${seededColorValue(
    `${seedString}-1`
  )}, ${seededColorValue(`${seedString}-2`)})`;
};

export function generateRandomPassword(length = 10) {
  let password = '';
  for (let i = 0; i <= length; i++) {
    const randomNumber = Math.floor(Math.random() * passwordRandomChars.length);
    password += passwordRandomChars[randomNumber];
  }
  return password;
}

export const getPhenotypeSearchURL = (searchTerm, currentPage) => {
  const URL = `${PHENOTYPES_CONSTANTS['hpoBaseURL']}${PHENOTYPES_CONSTANTS['searchHPOTerm']}?q=${searchTerm}&page=${currentPage}&limit=${PHENOTYPES_CONSTANTS['maxLimit']}`;
  return URL;
};

export const getHPOSearchURL = (searchTerm, currentPage, category) => {
  const URL = `${HPO_CONSTANTS['hpoBaseURL']}${HPO_CONSTANTS[
    'searchHPOTerm'
  ].replace(':category', category)}?q=${searchTerm}&page=${currentPage}&limit=${
    HPO_CONSTANTS['maxLimit']
  }`;
  return URL;
};

// check belongs genetics group
export const isGeneticsServiceUser = (userType) =>
  userType === CONSTANTS.userTypes.GENETICS ||
  userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN ||
  userType === CONSTANTS.userTypes.GENETICS_USER ||
  // TODO remove this admin check
  userType === CONSTANTS.userTypes.ADMIN;

export const isGeneticsServiceAdmin = (userType) =>
  userType === CONSTANTS.userTypes.GENETICS ||
  userType === CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN ||
  // TODO remove this admin check
  userType === CONSTANTS.userTypes.ADMIN;

export const getClinicianDiagnose = (
  id,
  clinicianDaignoses,
  diagnoseAge,
  diagnoseComments
) => {
  if (!clinicianDaignoses?.length) return null;
  const found = clinicianDaignoses.find((diagnose) => diagnose.id === id);
  if (!found) return null;
  const resultedDiagnose = { ...found };
  if (resultedDiagnose.id) {
    if (diagnoseAge && diagnoseAge[resultedDiagnose.id]) {
      resultedDiagnose.age = diagnoseAge[resultedDiagnose.id];
    }
    if (diagnoseComments && diagnoseComments[resultedDiagnose.id]) {
      resultedDiagnose.comment = diagnoseComments[resultedDiagnose.id];
    }
  }
  delete resultedDiagnose.diagnoseSpeciality;
  return resultedDiagnose;
};

export const isGeneId = (id) => id?.startsWith('NCBIGene:');
export const isPhenotypeId = (id) =>
  id?.startsWith('HP:') && id !== 'HP:999999';
export const isDiseaseId = (id) => !isGeneId(id) && !isPhenotypeId(id);

export const splitDiagnosesByType = (entries) => ({
  diseases: entries.filter((d) => isDiseaseId(d.id)),
  phenotypes: entries.filter((d) => isPhenotypeId(d.id)),
  genotypes: entries.filter((d) => isGeneId(d.id))
});

// Merge freshly-resolved picks into the entries already held, by id. An id that
// is already present is TOPPED UP with any field it is missing rather than
// skipped: the questionnaire autosaves as soon as a term is chosen, so the age
// (and comment) the patient types a moment LATER arrives against an id that
// already exists, and the old skip-on-collision discarded it — the proband's
// HPO/gene ages never reached the record at all (TGCLOUD-1574). A held value
// always wins over an incoming one, so a richer persisted record is never
// downgraded by getClinicianDiagnose's simplified shape.
//
// Known cost of held-wins, unchanged from the previous skip-on-collision and
// deliberately not addressed here: EDITING or CLEARING an age already stored on a
// proband phenotype still does not persist (the form reports "Saved" and keeps
// showing the new value), and un-ticking a term cannot remove it. Both need the
// form to post its entity arrays authoritatively rather than merging by id —
// a separate change, with its own risk of dropping clinician-owned fields.
export const mergeNewDiagnosesById = (existingArr = [], newArr = []) => {
  const out = [];
  const indexById = new Map();
  (existingArr || []).forEach((d) => {
    // An entry with no id can't be matched, so it passes straight through
    // (previous behaviour: every existing entry was always kept).
    if (!d?.id) {
      out.push(d);
      return;
    }
    indexById.set(d.id, out.length);
    out.push({ ...d });
  });
  (newArr || []).forEach((d) => {
    if (!d?.id || !indexById.has(d.id)) {
      out.push(d);
      return;
    }
    const held = out[indexById.get(d.id)];
    Object.entries(d).forEach(([key, value]) => {
      const missing =
        held[key] === undefined || held[key] === null || held[key] === '';
      if (missing && value !== undefined && value !== null && value !== '') {
        held[key] = value;
      }
    });
  });
  return out;
};

export const toLabel = (it, map) =>
  typeof it === 'string'
    ? map?.[it] ?? it
    : it?.name ?? it?.alias ?? it?.id ?? '';

export const toSearchText = (arr, map) =>
  Array.isArray(arr)
    ? arr
        .map((it) => {
          const label = toLabel(it, map);
          const id = typeof it === 'object' ? it?.id : undefined;
          return [label, id].filter(Boolean).join(' ');
        })
        .join(' | ')
    : '';

const uniqByKey = (arr) => {
  const seen = new Set();
  const out = [];
  const toKey = (item) =>
    typeof item === 'string'
      ? item
      : item && typeof item === 'object'
      ? item.id ?? item.name ?? item.alias ?? JSON.stringify(item)
      : '';
  for (const it of arr || []) {
    const k = toKey(it);
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(it);
    }
  }
  return out;
};

export const aggregateFamilyAnnotations = (members) => {
  const diseases = [],
    phenotypes = [],
    genotypes = [];
  (members || []).forEach((m) => {
    (m.diagnoses || []).forEach((d) => diseases.push(d));
    (m.phenotypes || []).forEach((p) => phenotypes.push(p));
    (m.genotypes || []).forEach((g) => genotypes.push(g));
  });

  return {
    diseases: uniqByKey(diseases),
    phenotypes: uniqByKey(phenotypes),
    genotypes: uniqByKey(genotypes)
  };
};

export const getPersonAge = (person) => {
  const direct = Number(person?.age);
  if (Number.isFinite(direct) && direct >= 0) return Math.floor(direct);

  const dobStr = person?.dob || person?.dateOfBirth || person?.birthDate;

  const calcAgeFromDate = (birthDate) => {
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) years--;
    return years >= 0 ? years : null;
  };

  if (dobStr) {
    const d = new Date(dobStr);
    if (!isNaN(d)) return calcAgeFromDate(d);
  }

  const yob = Number(person?.yob ?? person?.yearOfBirth);
  if (Number.isFinite(yob)) {
    const today = new Date();
    const years = today.getFullYear() - yob;
    return years >= 0 ? years : null;
  }
  return null;
};

export async function getPractitionerFromClient(client) {
  try {
    if (client.user && typeof client.user.read === 'function') {
      const practitionerRes = await client.user.read();
      return practitionerRes;
    }

    let fhirUser = null;

    if (typeof client.userInfo === 'function') {
      try {
        const userInfo = await client.userInfo();
        fhirUser = userInfo?.fhirUser || null;
      } catch (e) {
        console.warn(e);
      }
    }

    if (!fhirUser) {
      fhirUser =
        client?.state?.fhirUser ||
        client?.state?.tokenResponse?.fhirUser ||
        null;
    }

    if (!fhirUser) {
      return null;
    }

    const lower = fhirUser.toString().toLowerCase();
    if (!lower.includes('practitioner')) {
      return null;
    }

    const practitionerRes = await client.request(fhirUser);

    return practitionerRes;
  } catch (e) {
    return null;
  }
}

export const getPractitionerDisplay = (practitioner) => {
  if (!practitioner) return null;
  if (typeof practitioner === 'string') return practitioner;
  if (Array.isArray(practitioner.name) && practitioner.name.length > 0) {
    const n = practitioner.name[0];
    const given =
      Array.isArray(n.given) && n.given.length > 0 ? n.given.join(' ') : '';
    const family = n.family || '';
    const full = `${given} ${family}`.trim();
    if (full) return full;
  }
  return (
    practitioner.display ||
    practitioner.displayName ||
    practitioner.name ||
    null
  );
};

// dim color by 0.7 until luminance is legible.
export const getReadableTextColor = (hex) => {
  if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex))
    return hex || '#888';
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  for (let i = 0; i < 4 && 0.299 * r + 0.587 * g + 0.114 * b > 140; i++) {
    r = Math.round(r * 0.7);
    g = Math.round(g * 0.7);
    b = Math.round(b * 0.7);
  }
  return `rgb(${r}, ${g}, ${b})`;
};
